import { IConfigDto } from '@cuevideostory/core';
import { createDirectus, graphql, staticToken } from '@directus/sdk';

const CMS_URL = process.env.CMS_URL;
const BASE = process.env.SHOWROOM_SLUG;

interface Language {
  key: string;
  label?: string;
}

interface Languages {
  languages: Array<Language>;
  defaultLanguage: string;
}

interface GraphqlResult {
  languages: {
    code: string;
    name: string;
    is_default: boolean;
  }[];
  video_apps: VideoStoryCMS_Props[];
  video_app_scenes: VideoStoryCMSScene[];
}
let languageConfig: Languages;

interface VideoStoryCMSScene {
  id: string;
  name?: string;
  hide_name?: boolean;
  translations: VideoStoryCMSSceneTranslation[];
  position: string;
  exit_scene: {
    id: string;
  } | null;
  disable_transition?: boolean;
  links: VideoStoryCMSLink[];
  fade?: number | null;
  info_box?: VideoStoryCMSInfoBox;
}

interface VideoStoryCMSLink {
  id?: string;
  translations?: VideoStoryCMSLinkTranslation[];
  content_box_translations?: VideoStoryCMSLinkBoxTranslation[];

  component?: string;
  hover_overlay?: null;
  hover_blend?: null;
  hover_blend_mode?: null;
  x: number;
  y: number;
  anchor?: string;
  fade?: null;
  type?: string;
  icon?: null;
  scene?: { id: string; name?: string } | null;
  href?: string | null;
  disable_transition?: boolean;
  active?: boolean;
  topic?: {
    id: string;
  } | null;
}

interface VideoStoryCMSInfoBox {
  open?: boolean;
  translations: VideoStoryCMSInfoBoxTranslation[];
}
interface VideoStoryCMSInfoBoxTranslation {
  headline: string | null;
  description: string | null;
  cta_active: boolean;
  cta_label: string | null;
  cta_type: string | null;
  cta_href: string | null;
  cta_scene: { id: string; name: string } | null;
  languages_code: { code: string };
}
interface VideoStoryCMSSceneTranslation {
  scene_name: string | null;

  languages_code: { code: string };
}
interface VideoStoryCMSLinkTranslation {
  active: boolean;
  title: string | null;
  subtitle: string | null;
  href: string | null;
  languages_code: { code: string };
}
interface VideoStoryCMSLinkBoxTranslation {
  headline: string | null;
  content: string | null;
  button_label: string | null;
  languages_code: { code: string };
}
interface VideoStoryCMSTranslation_Props {
  media_base: string | null;
  languages_code: { code: string };
}
interface VideoStoryTranslation_Props {
  media_base: string | null;
  lang?: string;
}

interface VideoStoryCMS_Props {
  slug: string;
  name: string;
  start_scene: {
    id: string;
  };
  media_base: string;
  translations?: VideoStoryCMSTranslation_Props[];
  media_size: string[];
  ratio: number | string;
}

const transformCMSVideoStory = async (
  props: Omit<VideoStoryCMS_Props, 'slug' | 'name'> & {
    scenes: VideoStoryCMSScene[];
  } & {
    media_base_translations: VideoStoryTranslation_Props[];
  },
) => {
  const { scenes, media_base, media_base_translations, media_size, ratio, start_scene } = props;
  const transformedScenes = scenes.reduce((scenesObject, scene) => {
    const fade = scene.fade || 0;
    const scene_names: Record<string, string | undefined> = {};
    languageConfig.languages.forEach(({ key }) => {
      scene_names[key] = scene.hide_name
        ? undefined
        : scene?.translations?.find((t) => t.languages_code.code.split('-')?.[0] === key)
            ?.scene_name || scene.name;
    });

    const transformedScene = {
      ...scene,
      scene_name: scene_names,
      background_translations: transformBackgroundAudio(scene),
      info_box: transformInfoBox(scene),
      media: [
        {
          name: scene.position,
          links: true,
          loop: true,
          fade,
          formats: createMediaFormats({
            position: scene.position,
            media_base,
            media_base_translations,
            media_size,
            loop: true,
            ratio,
          }),
        },
      ],
      links: createSceneLinks({
        scene,
        scenes,
        media_base,
        media_base_translations,
        media_size,
        ratio,
      }),
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    scenesObject[transformedScene.id] = transformedScene;

    return {
      ...scenesObject,
      [transformedScene.id]: transformedScene,
    };
  }, {});

  //

  return { scenes: transformedScenes, startscene: start_scene.id };
};

const transformInfoBox = (scene: VideoStoryCMSScene) => {
  if (!scene?.info_box) return;

  return scene?.info_box?.translations.reduce((result, translation) => {
    const languageCode = translation.languages_code.code.split('-')?.[0];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    result[languageCode] = {
      headline: translation.headline,
      description: translation.description,
      open: scene?.info_box?.open,
      cta_type: translation.cta_type,
      cta_active: translation.cta_active,
      cta_label: translation.cta_label,
      cta_href:
        translation.cta_type === 'scene' || translation.cta_type === 'tourentry'
          ? translation?.cta_scene?.id
            ? `scene://${translation?.cta_scene?.id}`
            : null
          : translation?.cta_href || null,
    };

    return result;
  }, {});
};
const transformBackgroundAudio = (scene: VideoStoryCMSScene) => {
  // TODO
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!scene?.background) return null;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const ret = scene?.background?.translations.reduce((result, translation) => {
    const languageCode = translation.languages_code.code.split('-')?.[0];

    result[languageCode] = {
      formats: translation.formats,
    };

    return result;
  }, {});

  return ret;
};
const createMediaFormats = (props: {
  position: string;
  media_base: string;
  media_base_translations: VideoStoryTranslation_Props[];
  media_size: string[];
  loop?: boolean;
  ratio: number | string;
}) => {
  const { position, media_base, media_size, media_base_translations, loop, ratio } = props;
  const _pos = position.replace('GT', '');

  return media_size.map((ms) => {
    return {
      type: 'video/mp4',
      loop,
      src: `${media_base}/${ms}/${_pos}.mp4`,
      src_translations: media_base_translations.map(({ lang, media_base }) => ({
        lang,
        src: `${media_base}/${ms}/${_pos}.mp4`,
      })),
      width: parseFloat(ms) * (ratio as number),
      height: parseFloat(ms),
    };
  });
};

const createSceneLinks = (props: {
  scene: VideoStoryCMSScene;
  scenes: VideoStoryCMSScene[];
  media_base: string;
  media_base_translations: VideoStoryTranslation_Props[];
  media_size: string[];
  loop?: boolean;
  ratio: number | string;
}) => {
  const { scene, scenes, media_base, media_base_translations, media_size, ratio } = props;

  /*   const langCodes = languageConfig.languages.map((l) => l.key);
   */ const links = (scene.links || []) as VideoStoryCMSLink[];
  // ADD EXIT LINK
  if (scene.exit_scene && !scene.disable_transition) {
    links.push(createExitLink({ scene }));
  }

  return links.map((link) => ({
    ...extractLinkTranslationContents(link),
    /*  href:
      link?.type === "scene"
        ? langCodes.reduce((scenesHref, langCode) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            scenesHref[langCode] = `${link.type}://${link?.scene?.id}`;
            return scenesHref;
          }, {})
        : null, */
    formats:
      !link.disable_transition && (link.type === 'scene' || link.type === 'tourentry')
        ? createMediaFormats({
            position: `${scene.position}-${getPositionById({
              scenes,
              id: link?.scene?.id || '',
            })}`,
            media_base,
            media_base_translations,
            media_size,
            loop: false,
            ratio,
          })
        : null,
  }));
};

const getPositionById = ({ scenes, id }: { scenes: VideoStoryCMSScene[]; id: string }) => {
  return scenes.find((scene) => scene.id === id)?.position || null;
};

const createExitLink = (props: { scene: VideoStoryCMSScene }) => {
  const { scene } = props;
  return {
    x: 0,
    y: 0,
    type: 'scene',
    scene: scene.exit_scene,
  };
};
function extractLinkTranslationContents(link: VideoStoryCMSLink) {
  if (!link) return;
  const { ...linkProps } = { ...link };

  const translations = linkProps.translations || [];

  const langCodes: string[] = languageConfig.languages.map((l) => l.key);

  // TODO
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contents = translations?.reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (result: Record<string, any>, translation: any) => {
      const { languages_code, ...translationKeys } = translation;

      const currentLanguage = languages_code.code.split('-')?.[0] || null;

      Object.keys(translationKeys).forEach((translationKey) => {
        if (!(`${translationKey}` in result)) {
          result[translationKey] = {};
        }
        result[translationKey][currentLanguage] =
          translationKeys[translationKey as keyof typeof translationKeys];
      });
      return result;
    },
    {},
  );

  const { href, ...restOfTranslatedContent } = contents || {};
  const box_translations = linkProps.content_box_translations || [];

  const box_content = box_translations?.reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (result: Record<string, any>, translation: any) => {
      const { languages_code, ...translationKeys } = translation;

      const currentLanguage = languages_code.code.split('-')?.[0] || null;

      Object.keys(translationKeys).forEach((translationKey) => {
        if (!(`${translationKey}` in result)) {
          result[translationKey] = {};
        }
        result[translationKey][currentLanguage] =
          translationKeys[translationKey as keyof typeof translationKeys];
      });
      return result;
    },
    {},
  );
  return removeNullPropertiesFromObject({
    ...linkProps,
    contents: restOfTranslatedContent,
    content_box: box_content,
    href:
      link?.type === 'scene' || link?.type === 'tourentry'
        ? langCodes.reduce((scenesHref: Record<string, string>, langCode: string) => {
            scenesHref[langCode] = `scene://${linkProps?.scene?.id}`;
            return scenesHref;
          }, {})
        : href,
    active: linkProps?.active,
    component: linkProps?.component,
    hover_overlay: linkProps?.hover_overlay,
    topic: linkProps?.topic,
  });
}

function removeNullPropertiesFromObject(obj: object) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null));
}

export const fetchVideoStoryData = async (): Promise<IConfigDto | null> => {
  if (!CMS_URL) {
    console.error('NO CMS_URL');
    return null;
  }
  if (!process.env.CMS_ACCESS_TOKEN) {
    console.error('NO CMS_ACCESS_TOKEN');
    return null;
  }
  if (!process.env.SHOWROOM_SLUG) {
    console.error('NO SHOWROOM_SLUG');
    return null;
  }
  const client = createDirectus(`${CMS_URL}?nocache=1`)
    .with(staticToken(process.env.CMS_ACCESS_TOKEN))
    .with(graphql());

  const info_box = ` info_box {
    open
    translations {
      languages_code {
        code
      }
      headline
      description
      cta_active
      cta_label
      cta_type
      cta_scene {
        id
        name
      }
      cta_href
    }
  }`;

  const query = `query fetchVideoStoryConfig {
    languages {
      code
      name
      is_default
    }
      video_apps(filter: {slug: {_eq: "${BASE}"}}) {
        ratio
        slug
        media_base
        media_size
        start_scene {
          id
        }
        translations {
          media_base
          languages_code {
            code
          }
        }
      }
      video_app_scenes {
        id
        name
        fade
        hide_name
        disable_transition
        translations {
          scene_name
          languages_code {
            code
          }
        }
        position
        exit_scene {
          id
        }
        ${info_box}
        links {
          id
          active
          translations {
            active
            title
            subtitle
            href
            audio {
              id
              name
              loop
              volume
              # formats
            }
            languages_code {
              code
            }
          }
          component
          hover_overlay
          hover_blend
          hover_blend_mode
          x
          y
          anchor
          fade
          type
          is_grouped
          topic{
            id
          }
          icon {
            component
            name
          }
          icon_type {
            component
            name
          }
          scene {
            id
            name
          }
          content_box_enabled
          content_box_translations{
            languages_code{
              code
            }
            headline
            content
            button_label
          }
        }
        background {
          name
          loop
          volume
          # formats     
          translations{
            languages_code{
              code
            }
            # formats
          }     
        }
      }
    } `;

  const result = await client.query<GraphqlResult>(query);
  languageConfig = {
    languages: result.languages.map(({ code, name }) => ({
      key: code.split('-')[0],
      label: name,
    })),
    defaultLanguage: 'de',
  };

  const videoApp = result.video_apps?.[0];

  if (!videoApp) {
    console.log('No Video App found in CMS for SHOWROOM_SLUG:', BASE);
    return null;
  }

  const videoAppScenes = result.video_app_scenes;
  /*   const startscene = result.video_apps?.[0]?.start_scene?.id;
   */
  try {
    const { media_base, translations, media_size, ratio: ratioString, start_scene } = videoApp;
    const media_base_translations: VideoStoryTranslation_Props[] = [];
    if (translations) {
      translations.forEach((t) => {
        const { ...rest } = t;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        media_base_translations.push({
          ...rest,
          lang: t.languages_code.code.split('-')?.[0],
        });
      });
    }
    const parts = String(ratioString)?.split('/') || [16, 9];
    if (parts.length !== 2) {
      console.log('Invalid Ratio');
      return null;
    }

    const videoStory = await transformCMSVideoStory({
      scenes: videoAppScenes,
      media_base,
      media_base_translations,
      media_size,
      start_scene,
      ratio: parseFloat(parts[0]) / parseFloat(parts[1]),
    });

    if (!videoStory) {
      console.error('Could not fetch Video Story Config.');
      return null;
    }
    return videoStory;
  } catch (e) {
    console.error('Could not create Video Story JSON.', e);
    return null;
  }
};
